
import { Component, Prop, Vue } from "vue-property-decorator";
import { Getter, namespace } from "vuex-class";
import { FeaturesList, UserPermissions } from "@/store/types";
import Dispatch from "@/components/Dispatch.vue";
import DispatchMobile from "@/components/DispatchMobile.vue";
import ShortcutHelp from "./ShortcutHelp.vue";
import PutOnTest from "@/components/alarm-points/PutOnTest.vue";
import { EventDetails, EventShareUser } from "@/store/site-monitor/types";
import { get } from "lodash";
import api from "@/services/api.service";
import EventDetailsModal from "@/components/EventDetails.vue";
import { CaseData, CaseType } from "@/store/case-management/types.ts";
import vSelect from "vue-select";
import { stringMixin } from "@/mixins";
import EventTypes from "@/types/EventTypes";
import { PrefTypeRequest, PrefTypesEnum } from "@/store/settings/types";

const { isNullOrWhitespace } = stringMixin.methods;

	const SiteMonitor = namespace("siteMonitor");
	const Tasks = namespace("tasks");
	const AreaNotes = namespace("areaNotes");
	const CaseManagement = namespace("caseManagement");
	const SettingsStore = namespace("settings");

	@Component({
		components: {
			dispatch: Dispatch,
			"dispatch-mobile": DispatchMobile,
			"shortcut-help": ShortcutHelp,
			"put-on-test": PutOnTest,
			"event-details": EventDetailsModal,
			"v-select": vSelect
		}
	})
	export default class SiteMonitorHeader extends Vue {
		$refs: {
			extendButton: HTMLAnchorElement;
		};

		@Prop(Number) eventid?: number;

		@Getter("getFeature") getFeature: (featureName: string[]) => boolean;
		@Getter("getFeaturesList") featuresList: FeaturesList;
		@Getter("getPreviousRoute") returnPath: string;
		@Getter getPermissions: UserPermissions;
		@Getter getUserId: number;

		@SiteMonitor.Getter getEventRecords: any[];
		@SiteMonitor.Getter getEventShare!: any[];
		@SiteMonitor.Getter("getEventDetails") eventDetails: EventDetails;
		@SiteMonitor.Getter("getIsController") isController!: boolean;
		@SiteMonitor.Getter getEventRecordsAwaitingRestoreCount: any;
		@SiteMonitor.Getter getEventRecordsAwaitingAckCount: any;
		@SiteMonitor.Getter requiredMapPinIcon: boolean;
		@SiteMonitor.Getter areaNotesQuantity!: number;

		@SiteMonitor.Mutation setEventShareShown: any;
		@SiteMonitor.Mutation setEventLeaveShown: any;
		@SiteMonitor.Mutation setEventRaiseShown: any;
		@SiteMonitor.Mutation setEventCloseShown: any;
		@SiteMonitor.Mutation setEventDetailsShown: any;
		@SiteMonitor.Mutation setIncidentReportShown: any;
		@SiteMonitor.Mutation setPutEventOnTestShown: (shown: boolean) => void;

		@SiteMonitor.Action parkEvent: any;
		@SiteMonitor.Action("leaveEvent") leaveEventRequest: any;
		@SiteMonitor.Action setAreaNotesVisible: any;
		@SiteMonitor.Action fetchAreaNotes: any;
		@SiteMonitor.Action("updateSharing") updateSharingAction: any;
		@SiteMonitor.Action flagEventForReview: ({ eventId, flagged }) => Promise<boolean>;
		@SiteMonitor.Action createReviewRecord: ({ eventId, eventRecordTypeId, details }) => Promise<any>;
		@SiteMonitor.Action eventPassControl: any;

		@Tasks.Getter getOutstandingRequiredTasks: any;

		@AreaNotes.Getter getAreaNotesCount!: number;
		@AreaNotes.Action public fetchAreaNotesForEvent: (eventId: number) => Promise<void>;
		@AreaNotes.Mutation setAreaNoteGroupDetails: any;

		@CaseManagement.State caseTypes: CaseType[];
		@CaseManagement.Action fetchCaseTypes: (eventId: number) => void;

		@SettingsStore.Action getPrefsFromList: (prefEnumList: PrefTypeRequest[]) => void;
		@SettingsStore.Getter getPrefBoolValue: (prefType: PrefTypeRequest) => boolean;

		public dispatched: boolean = false;
		public hide: boolean = false;
		public isDestroyed: boolean = false;
		public updateInterval: NodeJS.Timeout = null;
		public isModalShown: boolean = false;
		private showTestEventConfirmation: boolean = false;


		/**
		 * Flag event modal state.
		 */
		public flagEventModalState = {
			open: false,
			processing: false
		};

		/**
		 * Data to send to case management.
		 */
		public caseData: CaseData = {
			EventId: null,
			TemplateId: null,
			Summary: null
		};

		/**
		 * Case type selected by user.
		 */
		public selectedCaseType: CaseType = null;

		/**
		 * Flag to show loading icon.
		 */
		public raisingToCases: boolean = false;

		public get sharedWith(): any[] {
			return this.getEventShare.filter(share => !share.removed);
		}

		public get canClose() {
			if (this.getEventRecordsAwaitingRestoreCount > 0) {
				return false;
			}

			if (this.getEventRecordsAwaitingAckCount > 0) {
				return false;
			}

			if (!this.isController) {
				return false;
			}

			if (this.getOutstandingRequiredTasks > 0) {
				return false;
			}

			if (this.requiredMapPinIcon) return false;

			return true;
		}

		public get closeTitle() {
			let restoreCount = this.getEventRecordsAwaitingRestoreCount;
			if (restoreCount > 0) {
				return restoreCount + " alarms awaiting restore.";
			}

			let ackCount = this.getEventRecordsAwaitingAckCount;
			if (ackCount > 0) {
				return ackCount + " alarms awaiting acknowledgement.";
			}

			if (!this.isController) {
				return "You do not have control of this event.";
			}

			if (this.getOutstandingRequiredTasks > 0) {
				return "There are still required actions left to complete.";
			}

			return "End Event";
		}

		public get raiseToCaseManagementEnabled() {
			return get(this.featuresList, ["CaseManagement", "RaiseToCaseManagement"]);
		}

		public get eventFlaggingEnabled() {
			return get(this.featuresList, ["Alarms", "SiteMonitor", "EventFlagging"]);
		}

		public get eventSharingEnabled() {
			return get(this.featuresList, ["Alarms", "SiteMonitor", "EventSharing"]);
		}

		public get sitRepEnabled() {
			return get(this.featuresList, ["Alarms", "SiteMonitor", "SitRep"]) && this.getPermissions.canViewSitRep;
		}

		public get raiseEventEnabled() {
			return get(this.featuresList, ["Alarms", "SiteMonitor", "Raise"]);
		}

		public get newAreaNotesEnabled() {
			return get(this.featuresList, ["AreaNotes"]) && get(this.featuresList, ["AreaNotes", "Icons"]);
		}

		public get areaNotesEnabled() {
			return get(this.featuresList, ["AreaNotes"]);
		}

		public get onTestEnabled() {
			return get(this.featuresList, "Alarms.AlarmPoints.OnTest");
		}

		public get siteTitle() {
			return this.eventDetails ? this.eventDetails.siteTitle : "";
		}

		public get groupId() {
			return this.eventDetails ? this.eventDetails.groupID : 0;
		}

		public get autoPassAlarmEnabled() {
			return get(this.featuresList, "Alarms.AutoPassAlarmOnLeave");
		}

		private get isSharingEnabled(): boolean {
			return get(this.featuresList, ["Alarms", "EventQueue", "DisableProcessingActiveAlarms"])
				? this.getPermissions.canProcessActiveAlarms
				: true;
		}

		public async created() {
			await this.fetchAreaNotesForEvent(this.eventid);
		}

		public async mounted() {
			this.updateSharing();

			if (this.raiseToCaseManagementEnabled) {
				await this.fetchCaseTypes(this.eventid);
			}

			const prefRequest: PrefTypeRequest = {
				prefTypeID: PrefTypesEnum.DisplayEndDialogTestEvents,
				groupID: null
			}
			await this.getPrefsFromList([prefRequest]);

			this.showTestEventConfirmation = this.getPrefBoolValue(prefRequest);
		}

		public updateSharing() {
			if (!this.isDestroyed) {
				this.updateSharingAction({ eventID: this.eventid }).then(() => {
					if (!this.isDestroyed) {
						this.updateInterval = setTimeout(this.updateSharing, 5000);
					}
				});
			}
		}

		public openSharing() {
			if (this.isController) {
				this.setEventShareShown(true);
			}
		}

		public beforeDestroy() {
			this.isDestroyed = true;
			if (this.updateInterval != null) {
				clearTimeout(this.updateInterval);
			}
		}

		public async leaveEvent() {
			if (this.eventDetails.eventTypeID == 6 || this.eventDetails.eventTypeID == 10) {
				this.$router.push({ path: "/event-queue" });
			} else {
				if (this.isController) {
					if (this.eventDetails.eventTypeID == 1) {
						if (this.autoPassAlarmEnabled) {
							//An issue at facebook allowed a user to autopass to themselves, meaning the modal never displayed
							// This should stop that from happening
							const allOperators = this.getEventShare.filter(
								(user: EventShareUser) => user.userID != null && !user.removed && user.isActive
							);

							let sharedOperators = this.getEventShare.filter(
								(user: any) =>
									user.userID != null && user.userID != this.getUserId && !user.removed && user.isActive
							);
							if (
								allOperators != null &&
								allOperators.length == 2 &&
								sharedOperators != null &&
								sharedOperators.length == 1
							) {
								await this.eventPassControl({ eventID: this.eventid, userID: sharedOperators[0].userID });
								// if this was opened from a new tab, close the window
								if (window.opener !== null) {
									window.close();
								} else {
									// send the user back to where they came from (if not set, return to event queue)
									let url = this.returnPath == "/" ? "/event-queue" : this.returnPath;
									this.$router.push({ path: url });
								}
							} else {
								this.setEventLeaveShown(true);
							}
						} else {
							this.setEventLeaveShown(true);
						}
					} else {
						await this.parkEvent({
							eventId: this.eventid,
							hours: 0,
							minutes: 0,
							seconds: 0,
							parkNote: null
						});
						this.$router.push({ path: "/event-queue" });
					}
				} else {
					await this.leaveEventRequest(this.eventid);
					this.$router.push({ path: "/event-queue" });
				}
			}
		}

		public raiseEvent() {
			if (this.isController) {
				this.setEventRaiseShown(true);
			}
		}

		public closeEvent() {
			if (!this.canClose || !this.isController) {
				return;
			}
			if (this.eventDetails.eventTypeID == 3) {
				//TODO: End test
			}
			// Event on test, confirmation off
			else if (!this.showTestEventConfirmation &&
				(this.eventDetails.eventTypeID == EventTypes.OnTest || this.eventDetails.eventTypeID == EventTypes.AuditTest)) {
					api.endTest(this.eventDetails.eventID).then(result => {
						this.$router.push({ path: "/event-queue" });
					});
			}
			//Regular event or test event with the confirmation on
			else {
				this.setEventCloseShown(true);
			}
		}

		/**
		 * Flags the event we're currently processing for review at a later stage.
		 */
		public async flagEvent() {
			if (!this.eventFlaggingEnabled) {
				return;
			}

			let successful = false;

			// Show our loading spinner
			this.flagEventModalState.processing = true;

			// If we're in control of the event, and it hasn't already been flagged
			if (this.isController && !this.eventDetails.flaggedForReview) {
				try {
					successful = await this.flagEventForReview({
						eventId: this.eventDetails.eventID,
						flagged: true
					});

					if (successful) {
						// If we flagged the event successfully, create an event record for audit
						await this.createReviewRecord({
							eventId: this.eventDetails.eventID,
							eventRecordTypeId: 109,
							details: "Flagged event for review"
						});
					}
				} catch (err) {
					console.error(err);
					successful = false;
				}
			}

			// Inform the user if we weren't able to flag the event for whatever reason
			if (!successful) {
				this.$notify({
					type: "error",
					title: "Flag for Review",
					text:
						"Unable to flag the current event for review - please try again later, or contact support if the problem persists."
				});
			}

			// Hide our spinner
			this.flagEventModalState.processing = false;
			this.flagEventModalState.open = false;
		}

		public showEventDetails() {
			this.setEventDetailsShown(true);
		}

		public incidentReport() {
			this.setIncidentReportShown(true);
		}

		/**
		 * Shows the flag event modal.
		 */
		public showFlagEventModal() {
			// Check to ensure event flagging is turned on- if it isn't, cancel
			if (!this.eventFlaggingEnabled) {
				return;
			}

			if (this.eventDetails && !this.eventDetails.flaggedForReview) {
				this.flagEventModalState.open = true;
			}
		}

		public unParkAtChanged(unParkAt: string) {
			if (unParkAt.endsWith("Z")) unParkAt = unParkAt.slice(0, -1);
			this.eventDetails.unParkAt = new Date(unParkAt);
			this.$refs.extendButton.click();
		}

		public get isMobileDispatchEnabled(): boolean {
			return this.getFeature(["Mobile", "Dispatch"]);
		}

		private showAreaNotesModal() {
			if (this.newAreaNotesEnabled) {
				this.setAreaNoteGroupDetails({
					groupId: this.groupId,
					groupTitle: this.siteTitle
				});
			}
			this.setAreaNotesVisible(true);
		}

		/**
		 * Open modal and reset fields.
		 */
		public showCaseManagementModal() {
			this.selectedCaseType = null;
			this.caseData.Summary = null;
			this.isModalShown = true;
		}

		/**
		 * Close modal.
		 */
		public closeCaseManagementModal() {
			this.isModalShown = false;
		}

		/**
		 * Method to raise event to case management.
		 */
		public async raiseToCaseManagement() {
			// When case type or summary are not defined, do not raise event to case management.
			if (this.selectedCaseType == null || isNullOrWhitespace(this.caseData.Summary)) {
				return;
			}
			// Set true to show loading icon.
			this.raisingToCases = true;
			// Update case data.
			this.caseData.EventId = this.eventid;
			this.caseData.TemplateId = this.selectedCaseType.id;

			// Raise event to cases.
			try {
				await api.raiseToCases(this.caseData);
			} catch (ex) {
				this.$notify({
					type: "error",
					title: "Error",
					text: ex.response.data
				});
			} finally {
				// In all cases, stop showing loading icon and close modal.
				this.raisingToCases = false;
				this.closeCaseManagementModal();
			}
		}
	}
